import { GroupedFilter } from 'module/types/filter-field/grouped-filter/filter';
import { isFieldFilterValid } from 'module/types/filter-field/grouped-filter/utils';
import { ApiGroupFilter } from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { UseQueryResult } from '@tanstack/react-query';
import { UseQueryOptions } from '@tanstack/react-query';
import { AllSignalsAPI } from 'api/all-signals';
import {
  AccountSignalsSidebarItemsResponse,
  SignalOccurenceLight,
  SignalOccurenceLightResponse,
} from 'api/all-signals/types';
import { SignalType } from 'api/signal/types';

const ALL_SIGNALS_QUERY_KEY = ['all-signals'];
const ACCOUNT_SIGNALS_SIDEBAR_QUERY_KEY = (
  accountId: number,
): AccountSignalsSidebarQueryKeyType => [
  'account-signals-sidebar',
  String(accountId),
];

type AccountSignalsSidebarQueryKeyType = readonly [
  'account-signals-sidebar',
  string,
];

interface GenerateSignalsApiUrlProps {
  filter: GroupedFilter;
  startRow: number;
  endRow: number;
  searchValue?: string;
  filterType?: string;
  sort?: string;
  companyId?: number;
  myAccountSignalType?: SignalType;
}

const generateSignalsApiUrl = ({
  filter,
  startRow = 0,
  endRow = PAGE_SIZE,
  searchValue,
  filterType,
  sort,
  companyId,
  myAccountSignalType,
}: GenerateSignalsApiUrlProps) => {
  const validFilter = filter?.groupCriteria?.filter((x) =>
    isFieldFilterValid(x.criteria),
  );

  const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(
    validFilter || [],
  );

  const queryParams = {
    ...(searchValue && { searchValue }),
    ...(sort && { sort: sort || 'signal_occurrence*createdAt.desc' }),
    ...(myAccountSignalType && { 'company*accountOwner.value': '12995' }),
    ...(companyId && { companyId }),
    offset: startRow,
    limit: endRow - startRow,
    sort: 'signal_occurrence*createdAt.desc',
  };

  const allQueryParams: string[] = [];

  if (filterAsString) {
    allQueryParams.push(filterAsString, `match=${filterType || 'AND'}`);
  }

  const otherParams = Object.entries(queryParams)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

  allQueryParams.push(...otherParams);

  const queryString = allQueryParams.join('&');

  return `/signal-occurrences/search${queryString ? `?${queryString}` : ''}`;
};

export const useGetSignalsByTypeQuery = (
  type: string,
  queryOptions?: Omit<
    UseQueryOptions<any, unknown, any, typeof ALL_SIGNALS_QUERY_KEY>,
    'queryKey'
  >,
): UseQueryResult<any, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: [...ALL_SIGNALS_QUERY_KEY, type],
    queryFn: () => AllSignalsAPI.getSignalsByType(type),
  });
};

export const useGetSidebarItemsQuery = (
  queryOptions?: Omit<
    UseQueryOptions<any, unknown, any, typeof ALL_SIGNALS_QUERY_KEY>,
    'queryKey'
  >,
): UseQueryResult<any, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: [...ALL_SIGNALS_QUERY_KEY, 'sidebar'],
    queryFn: () => AllSignalsAPI.getSidebarItems(),
  });
};

export const useGetAccountSignalsSidebarItemsQuery = (
  companyId: number,
  queryOptions?: Omit<
    UseQueryOptions<
      AccountSignalsSidebarItemsResponse,
      Error,
      AccountSignalsSidebarItemsResponse,
      AccountSignalsSidebarQueryKeyType
    >,
    'queryKey'
  >,
): UseQueryResult<AccountSignalsSidebarItemsResponse, unknown> => {
  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: ACCOUNT_SIGNALS_SIDEBAR_QUERY_KEY(companyId),
    queryFn: () => AllSignalsAPI.getAccountSignalsSidebarItems(companyId),
  });
};

const FILTERED_SIGNALS_QUERY_KEY = (filterAsString: string): string[] => [
  ...ALL_SIGNALS_QUERY_KEY,
  'filtered-signals',
  filterAsString,
];

export const useGetFilteredSignalsQuery = (
  params: {
    filter: GroupedFilter;
    startRow: number;
    endRow: number;
    signalConfigurationId?: number;
    signalType?: SignalType;
    searchValue?: string;
    filterType?: string;
    sort?: string;
    companyId?: number;
    myAccountSignalType?: SignalType;
  },
  queryOptions?: Omit<
    UseQueryOptions<
      SignalOccurenceLightResponse,
      unknown,
      SignalOccurenceLight[],
      typeof ALL_SIGNALS_QUERY_KEY
    >,
    'queryKey'
  >,
): UseQueryResult<SignalOccurenceLight[], unknown> => {
  const url = generateSignalsApiUrl({
    ...params,
  });

  return useQuery({
    ...(queryOptions ?? {}),
    queryKey: FILTERED_SIGNALS_QUERY_KEY(url),
    queryFn: () => AllSignalsAPI.getFilteredSignals(url),
    select: (response) => response.data,
  });
};

const PaginatedSignalsQueryKey = (filterAsString: string): string[] => [
  ...ALL_SIGNALS_QUERY_KEY,
  'paginated-signals',
  filterAsString,
];
const PAGE_SIZE = 100;

type PaginatedSignalsPageParam = { startRow: number; endRow: number };

export const useGetPaginatedSignalsQuery = (
  params: {
    filter: GroupedFilter;
    signalConfigurationId?: number;
    signalType?: SignalType;
    searchValue?: string;
    filterType?: string;
    sort?: string;
    companyId?: number;
    myAccountSignalType?: SignalType;
  },
  queryOptions: Omit<
    UseInfiniteQueryOptions<
      SignalOccurenceLightResponse,
      unknown,
      SignalOccurenceLight[],
      SignalOccurenceLightResponse,
      ReturnType<typeof PaginatedSignalsQueryKey>,
      PaginatedSignalsPageParam
    >,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  > = {},
): UseInfiniteQueryResult<SignalOccurenceLight[], unknown> => {
  const urlBase = generateSignalsApiUrl({
    ...params,
    filterType: params.filterType ?? 'AND',
    sort: params.sort ?? 'signal_occurrence*createdAt.desc',
    searchValue: params.searchValue ?? '',
    startRow: 0,
    endRow: PAGE_SIZE,
  });

  return useInfiniteQuery<
    SignalOccurenceLightResponse,
    unknown,
    SignalOccurenceLight[],
    ReturnType<typeof PaginatedSignalsQueryKey>,
    PaginatedSignalsPageParam
  >({
    queryKey: PaginatedSignalsQueryKey(urlBase),
    initialPageParam: { startRow: 0, endRow: PAGE_SIZE },
    queryFn: async ({ pageParam = { startRow: 0, endRow: PAGE_SIZE } }) => {
      const urlBase = generateSignalsApiUrl({
        ...params,
        filterType: params.filterType ?? 'AND',
        sort: params.sort ?? 'signal_occurrence*createdAt.desc',
        searchValue: params.searchValue ?? '',
        startRow: pageParam.startRow,
        endRow: pageParam.endRow,
      });

      return AllSignalsAPI.getFilteredSignals(urlBase);
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentDataCount = allPages.reduce(
        (acc, page) => acc + page.data.length,
        0,
      );

      if (!lastPage || currentDataCount >= lastPage.count) {
        return undefined;
      }

      return {
        startRow: currentDataCount,
        endRow: currentDataCount + PAGE_SIZE,
      };
    },
    select: (data) =>
      data.pages.reduce(
        (acc, page) => [...acc, ...page.data],
        [] as SignalOccurenceLight[],
      ),
    ...queryOptions,
  });
};
